import React, { useEffect, useMemo, useState } from "react";
import { Phone, PhoneCall, HeartPulse, ChevronRight } from "lucide-react";

const GOLD = "#c9a24b";

// Verbatim / historically-reconstructed narrative beats injected during descent.
// Sourced from Brian Clark's recorded account + Stanley Praimnath interview.
const CALL_STEPS = [
  { kind: "line", speaker: "Brian Clark", verbatim: true, text: "I called my wife to tell her, \u201cHere\u2019s where I am; we\u2019ll have this great celebration at home.\u201d She had just seen the second plane hit our building on the TV \u2014 she had no idea where I was." },
  { kind: "line", speaker: "Stanley Praimnath \u00b7 Voicemail", verbatim: true, text: "\u201cHoney, I\u2019m alive, but we\u2019re still inside.\u201d" },
  { kind: "dial", text: "Dial 9-1-1" },
  { kind: "hold", text: "Put on hold\u2026 they were completely overwhelmed." },
  { kind: "line", speaker: "911 Dispatcher \u00b7 1st", verbatim: true, text: "\u201cYou must tell your story to somebody higher up the chain.\u201d (the line clicks off)" },
  { kind: "hold", text: "On hold again\u2026 recite the story once more." },
  { kind: "line", speaker: "911 Dispatcher \u00b7 2nd", verbatim: true, text: "\u201cOh, just a minute. You must tell somebody else.\u201d" },
  { kind: "hold", text: "Asked a third time to repeat it all." },
  { kind: "line", speaker: "Brian Clark", verbatim: true, text: "\u201cNo. I have given you the details. Here they are one more time. I\u2019m gonna tell you this once, and then I\u2019m hanging up. Forty-fourth floor, man on the ground, need a medic, need a stretcher. Goodbye.\u201d" },
  { kind: "narr", text: "We were probably in that conference room for four minutes\u2026 and then it was back to the stairs." },
];

const SKY_STEPS = [
  { kind: "line", speaker: "Security Guard \u00b7 44th Sky Lobby", verbatim: false, text: "\u201cMy phone lines are down. When you reach a working phone, send a stretcher and medical help up here.\u201d" },
  { kind: "line", speaker: "Brian Clark", verbatim: false, text: "\u201cCome down with us \u2014 the stairs below are clear.\u201d" },
  { kind: "line", speaker: "Security Guard", verbatim: false, text: "\u201cI\u2019m not leaving him.\u201d (kneeling beside the wounded man)" },
];

export default function NarrativeGate({ gate, onComplete, onHaptic, onDialTone, officeImg, auto }) {
  const [step, setStep] = useState(0);
  const [lifted, setLifted] = useState(false);

  const isCall = gate === "call31";
  const steps = isCall ? CALL_STEPS : SKY_STEPS;
  const cur = steps[Math.min(step, steps.length - 1)];
  const atEnd = step >= steps.length - 1;

  // reset internal state whenever the gate changes
  const key = useMemo(() => gate, [gate]);

  const advance = () => {
    if (cur && cur.kind === "dial") { onDialTone && onDialTone(); onHaptic && onHaptic(false); }
    if (atEnd) { onComplete && onComplete(); setStep(0); setLifted(false); return; }
    setStep((s) => s + 1);
  };

  // Hands-free Guided Tour: auto-advance the gate beats, then complete.
  useEffect(() => {
    if (!auto || !gate) return;
    if (isCall && !lifted) { const l = setTimeout(() => { setLifted(true); onDialTone && onDialTone(); }, 1600); return () => clearTimeout(l); }
    const id = setTimeout(() => { advance(); }, 3400);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auto, gate, step, lifted]);

  const liftReceiver = () => {
    setLifted(true);
    onDialTone && onDialTone();
    onHaptic && onHaptic(false);
  };

  if (!gate) return null;

  return (
    <div
      key={key}
      data-testid={`narrative-gate-${gate}`}
      style={{
        position: "absolute", inset: 0, zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24, animation: "tab-fade 0.6s ease", pointerEvents: isCall ? "auto" : "none",
      }}
    >
      {/* environment backdrop — call31 keeps a photo; sky44 shows the live 3D lobby behind */}
      {isCall ? (
        <>
          <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${officeImg})`, backgroundSize: "cover", backgroundPosition: "center", filter: "brightness(0.45) contrast(1.05)" }} />
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(7,10,20,0.55), rgba(7,10,20,0.82))" }} />
        </>
      ) : (
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(7,10,20,0.12), rgba(7,10,20,0.5))", pointerEvents: "none" }} />
      )}

      {/* 44th-floor Sky Lobby — visible figures: wounded man on the ground + kneeling guard */}
      {!isCall && (
        <svg viewBox="0 0 400 150" style={{ position: "absolute", left: "50%", bottom: 18, transform: "translateX(-50%)", width: "min(520px,80vw)", height: "auto", opacity: 0.92, pointerEvents: "none" }}>
          <ellipse cx="200" cy="140" rx="180" ry="10" fill="rgba(0,0,0,0.45)" />
          {/* wounded man lying on the ground */}
          <g fill="#2a2d33" stroke="#e0533b" strokeWidth="0.6">
            <rect x="70" y="120" width="150" height="16" rx="8" />
            <circle cx="70" cy="128" r="12" />
            <rect x="66" y="112" width="14" height="8" rx="3" fill="#c0392b" stroke="none" />
          </g>
          {/* security guard kneeling beside him */}
          <g fill="#1f232b">
            <circle cx="250" cy="70" r="14" />
            <path d="M236 84 q14 -6 28 0 l6 34 q-20 8 -40 0 z" />
            <rect x="242" y="116" width="12" height="24" rx="4" />
            <rect x="258" y="116" width="12" height="24" rx="4" />
            <rect x="266" y="92" width="26" height="9" rx="4" transform="rotate(24 266 92)" />
            <circle cx="250" cy="70" r="14" fill="none" stroke="#c9a24b" strokeWidth="0.8" />
          </g>
        </svg>
      )}

      <div className="hud-panel" style={{ position: "relative", width: "min(620px, 92vw)", padding: "26px 28px 22px", borderRadius: 10, border: `1px solid ${GOLD}55`, pointerEvents: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <div style={{ width: 34, height: 34, borderRadius: 999, border: `1px solid ${GOLD}`, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(201,162,75,0.1)" }}>
            {isCall ? <Phone size={16} color={GOLD} /> : <HeartPulse size={16} color="#e0533b" />}
          </div>
          <div>
            <div style={{ fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase", color: GOLD }}>
              {isCall ? "31st Floor \u00b7 Oppenheimer & Co." : "44th Floor \u00b7 Sky Lobby"}
            </div>
            <div className="font-serif-q" style={{ fontSize: 17, color: "#f2e6c8", fontStyle: "italic" }}>
              {isCall ? "The 9-1-1 Calls" : "The Wounded Man & the Guard\u2019s Plea"}
            </div>
          </div>
        </div>

        <div style={{ height: 1, background: `${GOLD}33`, margin: "10px 0 16px" }} />

        {/* phone lift gate for the call scene */}
        {isCall && !lifted ? (
          <div style={{ textAlign: "center", padding: "18px 6px" }}>
            <p className="font-serif-q" style={{ color: "#d7cdb4", fontSize: 14, fontStyle: "italic", lineHeight: 1.55, marginBottom: 18 }}>
              A 2001-era desk landline rests on the conference table. Lift the receiver to place the calls.
            </p>
            <button
              data-testid="gate-lift-receiver"
              onClick={liftReceiver}
              className="gold-btn"
              style={{ padding: "13px 26px", borderRadius: 999, cursor: "pointer", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 9 }}
            >
              <PhoneCall size={16} /> Lift the Receiver
            </button>
          </div>
        ) : (
          <div style={{ minHeight: 138 }}>
            {cur && cur.kind === "hold" && (
              <div data-testid="gate-on-hold" style={{ textAlign: "center", padding: "10px 0 14px" }}>
                <div className="map-merge-pulse" style={{ display: "inline-block", padding: "6px 16px", borderRadius: 999, border: "1px solid #e0533b", color: "#e0533b", fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", fontFamily: "monospace" }}>
                  \u25cf On Hold
                </div>
                <p style={{ color: "#b9b3a4", fontSize: 13, marginTop: 14, fontStyle: "italic" }} className="font-serif-q">{cur.text}</p>
              </div>
            )}

            {cur && (cur.kind === "line") && (
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: GOLD }}>{cur.speaker}</span>
                  {cur.verbatim && <span style={{ fontSize: 8.5, letterSpacing: "0.1em", textTransform: "uppercase", color: "#7e8594", border: "1px solid #7e859455", padding: "1px 6px", borderRadius: 3 }}>Verbatim</span>}
                </div>
                <p className="font-serif-q" style={{ color: "#f4ecd6", fontSize: 17, lineHeight: 1.6 }}>{cur.text}</p>
              </div>
            )}

            {cur && cur.kind === "dial" && (
              <div style={{ textAlign: "center", padding: "26px 0" }}>
                <p className="font-serif-q" style={{ color: GOLD, fontSize: 18, letterSpacing: "0.1em" }}>{cur.text}</p>
                <p style={{ color: "#7e8594", fontSize: 12, marginTop: 8 }}>coincidence \u2014 9/11</p>
              </div>
            )}

            {cur && cur.kind === "narr" && (
              <p className="font-serif-q" style={{ color: "#d7cdb4", fontSize: 15, fontStyle: "italic", lineHeight: 1.6, textAlign: "center", padding: "14px 0" }}>{cur.text}</p>
            )}

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 18 }}>
              <span className="font-mono-tel" style={{ fontSize: 10, color: "#7e8594" }}>{step + 1} / {steps.length}</span>
              <button
                data-testid={atEnd ? "gate-complete" : "gate-next"}
                onClick={advance}
                className="gold-btn"
                style={{ padding: "11px 22px", borderRadius: 4, cursor: "pointer", fontSize: 12.5, display: "inline-flex", alignItems: "center", gap: 8 }}
              >
                {atEnd ? (isCall ? "Hang Up \u00b7 Return to Stairwell A" : "We Promise \u2014 Continue Down") : "Continue"}
                <ChevronRight size={15} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
